import React, { useEffect, useState } from "react";
import { Button, Modal, Pagination, PlusIcon } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import Preloader from "~/components/Preloader";
import Wrapper from "~/shared/ui/Wrapper";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import {
  IndicatorGroup,
  useGetIndicatorReportsLazyQuery,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2 } from "~/shared/ui/TableV2";
import { IndicatorGroupStatus } from "~/types/IndicatorGroupStatus";

import ManageReportForm from "./ManageReportForm";
import { tableHeader } from "./config/options";
import { tableDataFormatter } from "./config/tableDataFormatter";
import { getTemplatesOfReportsColumns } from "./model/tableAdapter";

interface IReport {
  groupId: string;
  groupStatus: string;
  onHandleClickByRows: (item: IndicatorGroup | null) => void;
}

const TemplatesOfReports = ({
  groupId,
  groupStatus,
  onHandleClickByRows,
}: IReport) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const [getReport, reportState] = useGetIndicatorReportsLazyQuery({
    fetchPolicy: "no-cache",
  });
  const history = useHistory();
  const { page, count, sortField, sortType, openModal, name } =
    useGetLocationData();

  const setDataReplace = useSetLocationData(true);
  const isOpenAddModal = openModal === "Add";
  const isOpenEditModal = openModal === "Edit";

  const { handleCount, handlePage } = usePaginationParams();

  const handleCloseModal = () => {
    setDataReplace("openModal", "", "reportId", "");
  };

  const handleRowEdit = (record: { id?: string | number }) => {
    setDataReplace("openModal", "Edit", "reportId", record.id);
  };

  const getReportTemplates = () =>
    getReport({
      variables: { GroupId: groupId, page, count, sortField, sortType },
    });

  useEffect(() => {
    if (groupId) getReportTemplates();
  }, [groupId, page, count, sortField, sortType]);

  const data = get(
    reportState.data,
    "IndicatorGroupReportGetter.getIndicatorReports",
    [],
  );

  const closeModal = () => history.replace(window.location.pathname);

  const goBack = () => {
    closeModal();
    onHandleClickByRows(null);
  };

  const openAddModal = () => setDataReplace("openModal", "Add");

  if (!data) {
    return <Preloader />;
  }

  const formattedTableItems = tableDataFormatter(data.items, t);
  const total = data.total;

  return (
    <Wrapper.Content
      title={`${t("reports.mainTitle")}: Каталог "${name}"`}
      txtBtn={t("ui.back")}
      goBackHandler={goBack}
      controlComponent={
        <Flex gap={10}>
          {hasPermission("indicatorGroupReports.indicatorReport.create") && (
            <Button
              onClick={openAddModal}
              size={"middle"}
              icon={<PlusIcon size={16} />}
            >
              {t("ui.add")}
            </Button>
          )}
        </Flex>
      }
    >
      <TableV2
        loading={reportState.loading}
        columns={getTemplatesOfReportsColumns(t, tableHeader)}
        dataSource={formattedTableItems}
        onRowEdit={
          hasPermission("indicatorGroupReports.indicatorReport.edit")
            ? handleRowEdit
            : undefined
        }
      />
      <Pagination
        count={count}
        page={page + 1}
        totalCount={total}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />
      <Modal
        title={
          isOpenAddModal
            ? t("reports_list.createIndicatorReport")
            : t("reports_list.editIndicatorReport")
        }
        open={isOpenAddModal || isOpenEditModal}
        onCancel={handleCloseModal}
        closable
        centered
        okButton={{
          htmlType: "submit",
          form: "ManageReportForm",
          children: t("ui.save"),
        }}
        destroyOnHidden
      >
        <ManageReportForm
          refetch={getReportTemplates}
          disabled={groupStatus === IndicatorGroupStatus.Removed}
          formId="ManageReportForm"
        />
      </Modal>
    </Wrapper.Content>
  );
};

export default TemplatesOfReports;
